import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, chakra, Heading, HeadingProps, StackProps, Text, TextProps, VStack } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import React from 'react';
const GRADIENT_TEXT_STYLES = {
  display: 'inline',
  backgroundImage: 'linear-gradient(90deg, #d97258 0%, #9858a3 100%)',
  backgroundClip: 'text',
  color: 'transparent'
} as const;
export interface GenericSectionProps {
  heading: string;
  gradientText?: string;
  subheading?: string;
  /** Section background. Defaults to `colorPalette.panel`. */
  background?: BoxProps['background'];
  colorPalette?: BoxProps['colorPalette'];
  children?: React.ReactNode;
  containerProps?: BoxProps;
  /** Props for the children stack (defaults to a spaced VStack). */
  contentProps?: StackProps;
  headerProps?: StackProps;
  headingProps?: HeadingProps;
  subheadingProps?: TextProps;
}

/**
 * GenericSection – A reusable landing-page section wrapper with a centered
 * heading/subheading opener and a content area for any child components.
 */

function renderHeadingWithGradient(heading: string, gradientText?: string) {
  if (!gradientText) {
    return heading;
  }
  const index = heading.indexOf(gradientText);
  if (index === -1) {
    return heading;
  }
  const before = heading.slice(0, index);
  const after = heading.slice(index + gradientText.length);
  return <>
      {before}
      <chakra.span {...GRADIENT_TEXT_STYLES}>{gradientText}</chakra.span>
      {after}
    </>;
}
export const GenericSection: React.FC<GenericSectionProps> = t0 => {
  const $ = _c(44);
  const {
    heading,
    gradientText,
    subheading,
    background: t1,
    colorPalette,
    children,
    containerProps,
    contentProps,
    headerProps,
    headingProps,
    subheadingProps
  } = t0;
  const background = t1 === undefined ? "colorPalette.panel" : t1;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t2;
  if ($[0] !== getBreakpointValue) {
    t2 = getBreakpointValue([3, 4, 6, 8]);
    $[0] = getBreakpointValue;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== getBreakpointValue) {
    t3 = getBreakpointValue([8, 12, 14, 14, 20, 24]);
    $[2] = getBreakpointValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== getBreakpointValue) {
    t4 = getBreakpointValue([4, 4, 6, 6]);
    $[4] = getBreakpointValue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== getBreakpointValue) {
    t5 = getBreakpointValue([2, 2, 4]);
    $[6] = getBreakpointValue;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== getBreakpointValue) {
    t6 = getBreakpointValue([8, 8, 10, 12]);
    $[8] = getBreakpointValue;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== getBreakpointValue) {
    t7 = getBreakpointValue(["2xl", "3xl", "4xl", "4xl", "5xl"]);
    $[10] = getBreakpointValue;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== gradientText || $[13] !== heading) {
    t8 = renderHeadingWithGradient(heading, gradientText);
    $[12] = gradientText;
    $[13] = heading;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== headingProps || $[16] !== t7 || $[17] !== t8) {
    t9 = <Heading as="h2" size={t7} textAlign="center" {...headingProps}>{t8}</Heading>;
    $[15] = headingProps;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== getBreakpointValue || $[20] !== subheading || $[21] !== subheadingProps) {
    t10 = subheading && <Text color="colorPalette.fg/70" fontSize={getBreakpointValue(["md", "lg", "xl"])} fontWeight="medium" maxWidth="container.lg" {...subheadingProps}>{subheading}</Text>;
    $[19] = getBreakpointValue;
    $[20] = subheading;
    $[21] = subheadingProps;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== headerProps || $[24] !== t10 || $[25] !== t5 || $[26] !== t6 || $[27] !== t9) {
    t11 = <VStack align="center" gap={t5} marginBottom={t6} maxWidth="container.md" textAlign="center" width="full" {...headerProps}>{t9}{t10}</VStack>;
    $[23] = headerProps;
    $[24] = t10;
    $[25] = t5;
    $[26] = t6;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== children || $[30] !== contentProps || $[31] !== getBreakpointValue) {
    t12 = children && <VStack align="stretch" gap={getBreakpointValue([8, 8, 10, 12])} width="full" {...contentProps}>{children}</VStack>;
    $[29] = children;
    $[30] = contentProps;
    $[31] = getBreakpointValue;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t11 || $[34] !== t12 || $[35] !== t4) {
    t13 = <VStack align="center" gap={t4} marginX="auto" maxWidth="container.xl" width="full">{t11}{t12}</VStack>;
    $[33] = t11;
    $[34] = t12;
    $[35] = t4;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== background || $[38] !== colorPalette || $[39] !== containerProps || $[40] !== t13 || $[41] !== t2 || $[42] !== t3) {
    t14 = <Box as="section" background={background} colorPalette={colorPalette} paddingX={t2} paddingY={t3} width="full" {...containerProps}>{t13}</Box>;
    $[37] = background;
    $[38] = colorPalette;
    $[39] = containerProps;
    $[40] = t13;
    $[41] = t2;
    $[42] = t3;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
export default GenericSection;